<template>
  <form @submit.prevent :novalidate="true">
    <b-card>
      <template v-slot:header>
        <div class="mb-3 row mb-0">
          <!-- Client Selection -->
          <div class="col-lg-6">
            <treeselect
              v-model="clientValue"
              :key="treeKey"
              :clearable="false"
              :placeholder="$t('Webportal.ClientFilter.Placeholder')"
              :options="clientItems"
              :disableFuzzyMatching="true"
              :disabled="isBusy"
              @input="treeSelectInput('clientValue', true, ...arguments)"
            />
          </div>
        </div>
      </template>

      <!-- Spinner overlay -->
      <div v-bind:class="{ spinner: isLoading }">
        <!-- Categories -->
        <div style="margin-bottom: 6rem">
          <p class="group-title">
            {{ $t("Webportal.CX.Dashboard.Exploration") }} - {{ $t("Webportal.CX.FirstAidKits") }}
          </p>

          <div class="group" v-for="(group, index) in groups" :key="index" v-bind:class="{ spinner: group.isLoading }">
            <!-- Name -->
            <b-row>
              <b-col>
                <h5>
                  {{ group.groupName }}
                </h5>
              </b-col>

              <b-col>
                <treeselect
                  ref="groupAmcSurvey"
                  :value="group.AmcSurvey"
                  :clearable="false"
                  :delete-removes="false"
                  :backspace-removes="false"
                  :placeholder="$t('Webportal.CX.Administration.AMCSurvey.Placeholder')"
                  :options="projectItems"
                  :disableFuzzyMatching="true"
                  :disableBranchNodes="true"
                  :disabled="isBusy || editingDisabled"
                  @input="firstAidGroupInput(group, index, $event)"
                />
              </b-col>
            </b-row>

            <hr />

            <h6>
              {{ $t("Webportal.CX.Administration.FirstAidKit.UploadLanguage") }}
            </h6>

            <b-row>
              <!-- Language Selection -->
              <b-col cols="2" align-self="end" class="mb-3">
                <treeselect
                  v-model="group.languageValue"
                  :clearable="false"
                  :placeholder="$t('Webportal.LanguageFilter.Placeholder')"
                  :options="group.languageItems"
                  :disableFuzzyMatching="true"
                  :disabled="isBusy || editingDisabled"
                />
              </b-col>

              <!-- File uploader -->
              <b-col>
                <file-uploader
                  upload-url="/api/administration/first-aid"
                  :value="group.FirstAidKits"
                  :upload-url-params="getUploadUrlParams(group)"
                  :file-rename-callback="getUploadFilename(group)"
                  :file-deletable-callback="disableDefaultDelete"
                  @file-removed="fileRemoved(group, ...arguments)"
                  @is-busy-changed="group.isBusy = $event"
                  :multiple="false"
                  :readonly="!group.languageValue"
                  accept="application/pdf"
                />
              </b-col>
            </b-row>

            <!-- bottom line, delete button -->
            <b-row>
              <b-col>
                <b-btn  class="float-end" size="md"
                        @click="tryDeleteFirstAidKitGroup(group)"
                        :disabled="group.isBusy"
                        variant="danger">
                  <font-awesome-icon :icon="['far', 'trash-alt']" class="me-2" /> {{ $t("Webportal.Delete") }}
                </b-btn>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>

      <!-- Add new FirstAidKit -->
      <template v-slot:footer>
        <b-row>
          <!-- Headline -->
          <b-col class="group-title">
            {{ $t("Webportal.CX.Administration.FirstAidKit.Upload") }}
          </b-col>
        </b-row>

        <b-row align-h="start">
          <!-- Group-File uploader -->
          <b-col cols="auto" class="pb-4" >
            <uploader ref="groupFileUploader"
              accept="application/pdf"
              :allowed-file-extensions="['.pdf']"
              :upload-url="fileToUploadUrl"
              :allow-delete="false"
              :value-changed="(e) => uploadValue = e"
              :disabled="isBusy || editingDisabled"
            />
          </b-col>

          <!-- Group-File upload status message -->
          <b-col cols="auto" align-self="end" class="ml-0 me-auto p-0 pt-0 pb-3 mb-2 mt-1" style="font-weight: 500">
            {{
              $t(fileToUpload ?
                "Webportal.CX.Administration.FirstAidKit.ReadyToUploadMessage" :
                "Webportal.CX.Administration.FirstAidKit.SelectFile")
            }}
          </b-col>

          <!-- AMC survey selection -->
          <b-col cols="5">
            <treeselect
              class=" pt-2"
              v-model="groupAMCSurveyValue"
              :clearable="false"
              :delete-removes="false"
              :backspace-removes="false"
              :placeholder="$t('Webportal.CX.Administration.AMCSurvey.Placeholder')"
              :options="projectItems"
              :disableFuzzyMatching="true"
              :disableBranchNodes="true"
              :disabled="isBusy || editingDisabled"
            />
          </b-col>
        </b-row>

        <b-row>
          <!-- GroupName -->
          <b-col>
              <!-- v-validate="'required'" -->

            <b-form-input type="text"
              size="md"
              v-model="groupNameValue"
              :placeholder="$t('Webportal.CX.FirstAidKitName')"
              :disabled="isBusy || editingDisabled"
              data-vv-name="groupName"
              trim
            />
          </b-col>

          <!-- Add button -->
          <b-col cols="auto">
            <button
              type="button"
              @click="addGroup"
              class="btn btn-primary"
              :disabled="isAddingDisabled || isBusy || editingDisabled"
            >
              + {{ $t("Webportal.CX.FirstAidKit") }}
            </button>
          </b-col>
        </b-row>
      </template>
    </b-card>

    <!-- Modal: Confirm Delete First Aid Kit Group -->
    <b-modal
      ref="modalConfirmDelete"
      :title="$t('Webportal.Dialog.Delete')"
      :ok-title="$t('Webportal.Dialog.Ok')"
      :cancel-title="$t('Webportal.Dialog.Cancel')"
      @ok="confirmDelete"
    >
      {{ pendingDeleteMessage }}
    </b-modal>
  </form>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import CancelToken from "@/mixins/CancelToken";

import AdministrationService from "@/services/AdministrationService";
import LocalizationsService from "@/services/LocalizationsService";
import SurveysService from "@/services/SurveysService";

import Uploader from "./Uploader"
import FileUploader from "@/components/FileUploader";

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';

export default {
  name: "CXFirstAidSettings",

  mixins: [AppConfig, CancelToken],

  components: {
    Uploader,
    FileUploader
  },

  data() {
    return {
      v$: null, // Vuelidate-Instanz
      treeKey: 0,

      clientItemsFull: [],
      clientItems: [],
      clientValue: null,

      uploadValue: null,
      groupNameValue: null,
      groupAMCSurveyValue: null,

      cancelToken: null,

      isGroupUploadBusy: false,
      isLoadingClients: false,
      isLoadingLanguages: false,
      isLoadingSettings: false,

      groups: [],

      pendingDeleteKit: null,
      pendingDeleteMessage: '',
    };
  },

  created() {
    this.v$ = useVuelidate.call(this);
  },

  mounted() {
    this.initializeTheme();
  },

  computed: {
    editingDisabled() {
      return _.isNil(this.clientValue);
    },

    isFormChanged() {
      return this.v$.$anyDirty;
    },

    isFormValid() {
      return !this.v$.$error || this.v$.$anyDirty;
    },

    isLoading() {
      return this.isLoadingClients || this.isLoadingLanguages || this.isLoadingSettings;
    },

    isBusy() {
      return this.isLoadingSettings || this.isGroupUploadBusy || this.groups?.some(g => g.isBusy);
    },

    isAddingDisabled() {
      return !this.fileToUpload || !this.groupNameValue || !this.groupAMCSurveyValue;
    },

    fileToUpload() {
      return this.uploadValue?.value?.[0];
    },

    fileToUploadLastModified() {
      const file = this.fileToUpload;
      return (file?.lastModified > 0 ? new Date(file.lastModified) : new Date()).toUTCString();
    },

    fileToUploadUrl() {
      return  `/api/administration/first-aid-group?` +
              `client-id=${this.clientValue}&` +
              `amc-survey=${this.groupAMCSurveyValue}&` +
              `group-name=${this.groupNameValue}&` +
              `last-modified=${this.fileToUploadLastModified}`;
    },

    projectItems() {
      return this.clientItemsFull?.find(c => c.id === this.clientValue)?._children || [];
    }
  },

  activated() {
    this.getSurveyStructure();
    this.getLanguages();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit("unsavedChanges", this.isFormChanged, next);
  },

  methods: {
    disableDefaultDelete(file) {
      return file?.name !== "deflt.pdf";
    },

    fileRemoved(group, file, fromServer, removed) {
      this.setGroupLanguageStatus(group, file?.name?.slice(0, -4), !removed);
    },

    setGroupLanguageStatus(group, id, isDisabled) {
      // Allows to grey out language elements
      const index = group.languageItems.findIndex(l => l.id == id);
      if (index > -1) {
        group.languageItems[index].isDisabled = isDisabled;
      }
    },

    getUploadFilename(group) {
      return (file) => {
        this.setGroupLanguageStatus(group, group.languageValue, true);

        const fileName = group.languageValue + ".pdf";
        group.languageValue = null;
        return fileName;
      };
    },

    getUploadUrlParams(group) {
      return (file) => ({
        "group-id": group?.groupId,
        "file-name": file?.name,
        "last-modified": new Date(file?.lastModified || Date.now()).toUTCString()
      });
    },

    // Get survey structure data to extract all clients
    getSurveyStructure() {
      this.isLoadingClients = true;

      return SurveysService.getSurveyStructure(null, true)
        .then((response) => {
          this.clientItems = this.parseSurveyStructure(response.data);
          this.treeSelectInput('clientValue', true, this.clientItems[0].id);
          this.treeKey++;
        })
        .then(() => SurveysService.getSurveyStructure(null, false, true))
        .then((response) => { this.clientItemsFull = this.parseSurveyStructure(response.data); })
        .finally(() => { this.isLoadingClients = false; });
    },

    parseSurveyStructure(structure) {
      return structure
        // Filter for clients only
        ?.filter(c => c.id?.startsWith('C'))
        ?.map(c =>
          ({
            id: c.id.substring(1),
            label: c.label,

            // Survey per client
            _children: c.children
              ?.map(p =>
                (
                  // Without projects
                  p.id?.startsWith('S') ?
                  {
                    id: p.id.substring(1),
                    label: p.label,
                  } :
                  // With projects
                  {
                    ...p,
                    children: p.children
                      ?.filter(s => s.id?.startsWith('S')) // Only survey children
                      ?.map(s =>
                        ({
                          id: s.id.substring(1),
                          label: s.label
                        })
                      ) || []
                  }
                )
              ) || []
          })
        );
    },

    getLanguages() {
      this.isLoadingLanguages = true;

      return LocalizationsService.getLocalizationTexts().then((response) => {
        this.languageItems = _(response.data)
          .groupBy("localizationTextId")
          .keys()
          .map(lang => ({ id: lang, label: lang }))
          .value();
      })
      .finally(() => { this.isLoadingLanguages = false; });
    },

    getSettings() {
      if (!this.clientValue) return;

      this.isLoadingSettings = true;
      this.cancelToken = this.initCancelToken(this.cancelToken);

      this.groups = [];
      this.$refs.groupFileUploader.deleteFile();
      this.groupNameValue = null;

      return AdministrationService.getCXClientSettings(this.clientValue, this.cancelToken)
        .then(response => {
          if (!response?.data) {
            console.warn('Got empty response for settings.');
            return;
          }

          if (response.data.FirstAidKitGroups) {
            this.groups = response.data.FirstAidKitGroups.map(group =>
              ({
                ...group,
                languageItems: this.languageItems.map(lang =>
                  ({
                    ...lang,
                    isDisabled: group.FirstAidKits?.some(fak => fak.name?.startsWith(lang.id))
                  })
                ),
                languageValue: null,
                isBusy: false
              }),
            );
          }

          this.v$.$reset();
        })
        .catch(error => console.warn("Error while load cx settings: " + error))
        .finally(() => {
          this.isLoadingSettings = false;
          this.cancelToken = null;
        });
    },

    firstAidGroupInput(group, index, survey, name) {
      // Prevent recursion
      if (group.isLoading) return;
      this.groups[index] = { ...group, isLoading: true };

      const oldSurvey = group.AmcSurvey;
      const oldGroupName = group.GroupName;

      return AdministrationService.updateFirstAidKitGroup(
        group.groupId,
        {
          AmcSurvey: survey,
          GroupName: name
        })
        .then(() => {
          if (survey) group.AmcSurvey = survey;
          if (name) group.GroupName = name;
        })
        .catch((e) => {
          console.error("Failed to update group", e);

          // Reset tree selection on failure
          this.$refs.groupAmcSurvey[index].select({ id: oldSurvey });
        })
        .finally(() => {
          this.groups[index] = { ...group, isLoading: false };
        });
    },

    // Called whenever the input of the structure treeselect changes
    treeSelectInput(valueField, getSettings, value) {
      if (this[valueField] === value) return;

      let currentValue = this[valueField];
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        // Cancel
        if (result === false) {
          this[valueField] = currentValue;
        }
        // User discarded changes or form is not dirty
        else if (result == null) {
          this[valueField] = value;
        }
      });

      if (getSettings)
        this.getSettings()
    },

    cancel() {
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        if (result !== false) {
          //this.getSettings();
        }
      });
    },

    tryDeleteFirstAidKitGroup(kit) {
      // Ask DB if there are any resolutions attached to this group
      kit.isBusy = true;
      AdministrationService.hasFirstAidKitGroupResolutions(kit.groupId)
        .then((res) => {
          this.showConfirmDeleteMsgBox(kit, res.data.hasResolutions);
        })
        .catch((error) => {
          console.error("Error while deleting First Aid Kit Group: " + error);
        })
        .finally(() => {
          kit.isBusy = false;
        });
    },

    showConfirmDeleteMsgBox(kit, hasResolutions) {
      // Speichere Kit und Nachricht für die Confirmation
      this.pendingDeleteKit = kit;
      this.pendingDeleteMessage = this.getConfirmMessage(hasResolutions);

      // Zeige Modal
      this.$refs.modalConfirmDelete.show();
    },

    confirmDelete() {
      // Wird aufgerufen, wenn der User OK klickt
      if (this.pendingDeleteKit) {
        this.deleteFirstAidKitGroup(this.pendingDeleteKit);
        this.pendingDeleteKit = null;
        this.pendingDeleteMessage = '';
      }
    },

    deleteFirstAidKitGroup(kit) {
      AdministrationService.deleteFirstAidKitGroup(kit.groupId)
        .then((res) => {
          this.groups = this.groups.filter(g => g.groupId !== kit.groupId);
        });
    },

    getConfirmMessage(hasResolutions) {
      return hasResolutions
        ? 'There are Resolutions attached to this First Aid Kit Group. ' +
          'Please confirm that you want to delete everything.'
        : 'There is nothing attached to this First Aid Kit Group. ' +
          'Please confirm that you want to delete everything.';
    },

    addGroup() {
      this.isGroupUploadBusy = true;

      return this.$refs.groupFileUploader.uploadFile()
        .then((e) => {
          if (!e) throw "No event data";
          const response = JSON.parse(e.request?.response);
          const file = e.file;

          if (!response || !(file instanceof File))
            throw "Event data invalid";

          // Add group without reloading
          this.groups.push({
            groupId: response.groupId,
            groupName: this.groupNameValue,

            isBusy: false,
            languageValue: null,
            languageItems: this.languageItems.map(lang =>
              (lang.id === "deflt") ? { ...lang, isDisabled: true } : lang
            ),

            FirstAidKits: [
              {
                  "id":         response.id,
                  "createdAt":  new Date().toUTCString(),
                  "modifiedAt": new Date(e.file?.lastModified || Date.now()).toUTCString(),
                  "name":       "deflt.pdf",
                  "size":       file.size
              }
            ]
          });

          // Reset form
          this.groupNameValue = null;
          this.$refs.groupFileUploader.deleteFile();
        })
        .catch((e) => console.error("Failed to upload group file", e))
        .finally(() => { this.isGroupUploadBusy = false; });
    }
  }
};
</script>

<style lang="scss" scoped>
.group-title {
  font-size: 1.3rem;
}
.group {
  margin: 1.5rem 0.5rem 2.5rem 0.5rem;
  padding: 1.5rem;
  border: 1px solid #ccc;
  border-radius: calc(0.75rem - 1px);
}
.card-footer:last-child {
  border-radius: 0 0 0.25rem 0.25rem;
  border-top: 1px solid var(--color-accent);
}
</style>
